import { Eraser, Pen, Redo2, Trash2, Undo2, ZoomIn, ZoomOut } from 'lucide-react';
import { getStroke } from 'perfect-freehand';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

type InkPoint = [number, number, number];

interface Stroke {
  points: InkPoint[];
  size: number;
  /** Cached outline for a committed stroke — see `finalizeStroke`. Never set on the active stroke. */
  outline?: number[][];
}

/**
 * One saved stroke. Coordinates are FRACTIONS OF THE ANSWER REGION, not pixels: the same drawing has to
 * come back correctly in a different window, on a different screen, or after the exam page reflows, and the
 * region's own aspect ratio is fixed by what was printed. `size` is a fraction of the region's width for the
 * same reason. Points are flattened to x, y, pressure triples — a draft is written to localStorage on every
 * change, and the array-of-arrays form roughly doubles its size for nothing.
 */
export interface SavedStroke {
  p: number[];
  s: number;
}

/** An answer box as a fraction of the ONE printed page it sits on — see `pageImageUrl`. */
export interface PageCrop {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface DrawingCanvasProps {
  onChange: (dataUrl: string | null, strokes: SavedStroke[]) => void;
  /**
   * The strokes of an already-saved drawing for this slot. Taken once, on the first render that has both a
   * measured region and something to restore — after that this component owns the strokes, and its own
   * export round-tripping back in through here is ignored.
   */
  initialStrokes?: SavedStroke[];
  /**
   * A saved drawing that has no strokes with it — a draft from before strokes were saved. Restored as a flat
   * image under anything drawn now, so old work still shows up; unlike `initialStrokes` it can only be
   * removed wholesale with Clear, since there are no strokes in it for undo or the eraser to find.
   */
  initialDrawing?: string | null;
  /**
   * The PNG of the one printed page this answer box sits on (`StudentExamPayload.pageImageUrls`), and this
   * region's rectangle in it. A RASTER, not the live exam SVG: cropping and scaling a bitmap via plain CSS
   * (`printedBackground` below) is a cheap texture-sample regardless of how far it's blown up, where doing
   * the same to inline vector content means re-rasterizing every path at that size — the earlier version of
   * this feature cropped the SVG instead and had to fight that cost (a narrowed `viewBox`, a stripped
   * text-selection layer, one blob per slot). None of that is needed for a picture that was already
   * rasterized once, at save time, the same way the grading and structure-editor views already show it.
   */
  pageImageUrl?: string;
  crop?: PageCrop;
  /**
   * PICTURES of the task's own printed prompt (not its answer region) — one entry per ancestor for a
   * subtask, root to leaf, e.g. task "5"'s own intro THEN "5.a"'s own prompt for the drawing on "5.a"'s
   * answer — stacked with a small gap above the printed backdrop while the zoomed editor is open. The
   * inline preview is often too small to read the question at all, and closing the editor to re-check it
   * interrupts the drawing. Pictures, not `task.description`'s raw text: the printed prompt can carry
   * formatting (formulas, wrapped figures) a plain string can't, and showing the actual page content can
   * never drift from what's really printed. Each entry carries its OWN page image (an ancestor CAN be on an
   * earlier page than the leaf's own answer — a sibling subtask's growing answer can push a later one onto
   * the next page) alongside the crop fractions and the box's real aspect ratio (`PageCrop`'s two fractions
   * are each relative to a DIFFERENT page dimension, so they aren't directly comparable to each other for
   * sizing). Absent/empty whenever there's nothing to carve a prompt out of, in which case nothing is
   * rendered — this is a convenience, not a requirement.
   */
  promptCrops?: { crop: PageCrop; aspect: number; pageImageUrl: string }[];
}

const MAX_ZOOM = 4;
// Export resolution: `DETAIL` backing pixels per region pixel, bounded by a total and a side. These are
// what keep the close of an editing session from being a visible freeze — the old values let a page-sized
// sketch region come out as a 12 MEGAPIXEL canvas that had to be allocated, painted, PNG-encoded and
// base64'd into a draft on every close (measured: a 525KB data URL and ~37ms of script per open/close round
// trip). What actually matters is the resolution the ink is GRADED and REPRINTED at: 3M pixels across a
// 475pt wide region is ~230 DPI, in the range exam scans are digitised at to begin with, and a small answer
// box still gets 6 output pixels per on-screen pixel.
const DETAIL = 6;
const MAX_EXPORT_PIXELS = 3_000_000;
const MAX_EXPORT_SIDE = 2600;
const INK = '#0f172a';

// The full-pressure diameter fed to perfect-freehand (region-space units, same space `points` are stored
// in) — bumped up from the old flat 0.9 CSS-px line width because `thinning` below means most of a stroke
// renders thinner than this, not at it.
const NIB_SIZE = 1.3;
// Backing-store pixels per CSS pixel. Capped at 2 because this multiplies AREA: the editor fills most of the
// screen, and it holds two canvases (the visible one and the committed layer), each of which is re-allocated
// on open, cleared and redrawn on every sample, and handed to the compositor on every frame. A previous 2x
// supersample on top of the device ratio made all of that four times as expensive for a difference in edge
// quality that canvas's own antialiasing already covers — measured at 8ms per open/close cycle in
// compositor upload alone.
const MAX_PIXEL_RATIO = 2;

// Native `cursor: crosshair`/`cursor: cell` are OS-drawn cursors whose appearance (including color/contrast
// inversion) is decided by the OS's own pointer-type theming — Windows in particular renders them
// differently for mouse vs. pen/Windows Ink hover. A custom SVG cursor renders identically regardless of
// input device, so we draw our own fixed black-on-white ring/square instead of trusting the OS one.
function svgCursor(shape: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'>${shape}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 8 8, crosshair`;
}
const PEN_CURSOR = svgCursor(
  `<g stroke='white' stroke-width='3'><line x1='8' y1='0' x2='8' y2='6'/><line x1='8' y1='10' x2='8' y2='16'/><line x1='0' y1='8' x2='6' y2='8'/><line x1='10' y1='8' x2='16' y2='8'/></g>` +
    `<g stroke='black' stroke-width='1.25'><line x1='8' y1='0' x2='8' y2='6'/><line x1='8' y1='10' x2='8' y2='16'/><line x1='0' y1='8' x2='6' y2='8'/><line x1='10' y1='8' x2='16' y2='8'/></g>`,
);
const ERASER_CURSOR = svgCursor(
  `<rect x='2' y='2' width='12' height='12' fill='none' stroke='white' stroke-width='3'/><rect x='2' y='2' width='12' height='12' fill='none' stroke='black' stroke-width='1.25'/>`,
);
// See `idleFinalizeTimeoutRef` — long backstop only, for a contact that never resumes at all.
const STROKE_IDLE_MS = 4000;
// Below this gap, always treat a new sample as continuing the active stroke, no distance check.
const CONTINUATION_GRACE_MS = 200;
// Past the grace period, only continue the active stroke if the new sample lands within this many region
// (CSS-scale) px of where it left off; farther than this is treated as a separate, new stroke instead of
// being connected with a straight line across whatever distance the gap covered.
const CONTINUATION_MAX_JUMP = 32;

function isEraserButton(event: PointerEvent): boolean {
  if (event.pointerType !== 'pen') return false;
  const ERASER_OR_BARREL = 0b100010;
  return (event.buttons & ERASER_OR_BARREL) !== 0 || event.button === 2 || event.button === 5;
}

function distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * perfect-freehand options. The one non-default value that matters most: `streamline: 0`. Streamline is a
 * moving-average smoothing pass over the raw points *before* the outline is built — the earlier attempt at
 * this library used the 0.5 default and it is what made fast strokes "correct my curves to lines": a sharp
 * apex measured 16.5px shorter than drawn once streamline pulled the path toward its own average. Thinning
 * (pressure -> width) and the outline curve fitting perfect-freehand does internally are unaffected by this
 * — turning streamline off keeps the tapered, pressure-sensitive look without any lag or corner-cutting.
 */
function freehandOptions(size: number, last: boolean) {
  return {
    size,
    thinning: 0.5,
    smoothing: 0.65, // rounds the start/end taper a bit more; unrelated to `streamline`, safe to raise
    streamline: 0,
    simulatePressure: true,
    last,
  };
}

/**
 * Turns perfect-freehand's raw outline vertices into a smooth closed path. A plain `lineTo` between every
 * vertex looks faceted/jagged even though the underlying stroke is geometrically exact (see `streamline: 0`
 * above) — outline vertices are spaced far finer than a single pen sample, so the polygon's own edges show
 * as tiny visible bends. The standard fix (perfect-freehand's own examples ship an equivalent
 * `getSvgPathFromStroke` helper) is to draw a quadratic curve from each vertex to the *midpoint* of it and
 * the next one, using the vertex itself as the control point — this rounds the outline's edges at a
 * sub-sample granularity without cutting into the actual drawn path the way `streamline` did.
 */
function outlineToPath2D(outline: number[][]): Path2D {
  const path = new Path2D();
  const len = outline.length;
  if (len === 0) return path;
  if (len < 4) {
    // Too few vertices for the midpoint technique below (e.g. a single tap's small round outline).
    path.moveTo(outline[0]![0]!, outline[0]![1]!);
    for (let i = 1; i < len; i++) path.lineTo(outline[i]![0]!, outline[i]![1]!);
    path.closePath();
    return path;
  }

  const first = outline[0]!;
  let a = outline[1]!;
  const b = outline[2]!;
  path.moveTo(first[0]!, first[1]!);
  path.quadraticCurveTo(a[0]!, a[1]!, (a[0]! + b[0]!) / 2, (a[1]! + b[1]!) / 2);

  for (let i = 2; i < len - 1; i++) {
    a = outline[i]!;
    const next = outline[i + 1]!;
    path.quadraticCurveTo(a[0]!, a[1]!, (a[0]! + next[0]!) / 2, (a[1]! + next[1]!) / 2);
  }

  path.closePath();
  return path;
}

type Region = { width: number; height: number };

// Five decimals of a fraction is well under a printer dot on a page-sized region, and keeps a draft's JSON
// from carrying seventeen digits per coordinate.
const round = (value: number) => Math.round(value * 1e5) / 1e5;

/** See `SavedStroke` — region pixels out to region fractions. */
function toSavedStroke(stroke: Stroke, region: Region): SavedStroke {
  const p: number[] = [];
  for (const [x, y, pressure] of stroke.points) {
    p.push(round(x / region.width), round(y / region.height), round(pressure));
  }
  return { p, s: round(stroke.size / region.width) };
}

/** The inverse, with the outline built up front so repaints, undo and erase never have to recompute it. */
function fromSavedStroke(saved: SavedStroke, region: Region): Stroke {
  const points: InkPoint[] = [];
  for (let i = 0; i + 2 < saved.p.length; i += 3) {
    points.push([saved.p[i]! * region.width, saved.p[i + 1]! * region.height, saved.p[i + 2]!]);
  }
  const size = saved.s * region.width;
  return { points, size, outline: getStroke(points, freehandOptions(size, true)) };
}

/** Moves a stroke between two sizes of the same region — see `measureRegion`, where the box a drawing was
 * made in can change under it (a window resize, the exam page reflowing). */
function rescaleStroke(stroke: Stroke, from: Region, to: Region): Stroke {
  const scaleX = to.width / from.width;
  const scaleY = to.height / from.height;
  const points = stroke.points.map(([x, y, pressure]) => [x * scaleX, y * scaleY, pressure] as InkPoint);
  const size = stroke.size * scaleX;
  return { points, size, outline: stroke.outline ? getStroke(points, freehandOptions(size, true)) : undefined };
}

function fillOutline(context: CanvasRenderingContext2D, outline: number[][]) {
  if (outline.length === 0) return;
  context.fillStyle = INK;
  context.fill(outlineToPath2D(outline));
}

/** Fills a finished stroke — uses the outline cached at commit time (see `finalizeStroke`) so undo/redo/erase
 * repaints redraw it without recomputing the geometry. */
function paintStroke(context: CanvasRenderingContext2D, stroke: Stroke) {
  if (stroke.points.length === 0) return;
  fillOutline(context, stroke.outline ?? getStroke(stroke.points, freehandOptions(stroke.size, true)));
}

/** Fills the live, still-growing stroke. Always `last: false` — capping the end early would show a tapered
 * point mid-stroke that then has to "reopen" on the next sample. */
function paintActiveStroke(context: CanvasRenderingContext2D, stroke: Stroke) {
  if (stroke.points.length === 0) return;
  fillOutline(context, getStroke(stroke.points, freehandOptions(stroke.size, false)));
}

/**
 * A click-to-edit drawing widget: the inline surface is a non-drawable preview button (see `canEdit`) —
 * clicking it opens a zoomed, full-size editor where the actual drawing happens. `onChange` fires exactly
 * once, when that editor closes, never mid-stroke — the source of every "why is this laggy" bug chased down
 * in earlier rounds was some path that notified the parent (and so triggered a page-level re-render) during
 * active drawing. Manages the strokes internally via refs; the only answer value it reads back is the saved
 * drawing, once, to pick up work from before this page load.
 */
export const DrawingCanvas = memo(function DrawingCanvas({
  onChange,
  initialStrokes,
  initialDrawing,
  pageImageUrl,
  crop,
  promptCrops,
}: DrawingCanvasProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const regionElRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const strokesRef = useRef<Stroke[]>([]);
  // Strokes restored from a previous session (see `initialStrokes`). Kept apart from this session's so the
  // eraser can reach them while undo and redo can't: undoing work you did before the page reloaded isn't
  // something the toolbar promises, and stepping backwards through a stack you can't see is worse than not
  // being able to. Everything else treats the two lists the same.
  const restoredRef = useRef<Stroke[]>([]);
  const redoRef = useRef<Stroke[]>([]);
  // See `emit` — guards against an older, still-in-flight async export landing after a newer one (closing,
  // reopening, and closing the editor again fast enough can overlap two exports).
  const emitSeqRef = useRef(0);
  const activeRef = useRef<Stroke | null>(null);
  const committedRef = useRef<HTMLCanvasElement | null>(null);
  // See `initialDrawing` — previously saved ink, painted under this session's strokes by `repaintCommitted`
  // and included in the export. It isn't a `Stroke`, so undo/redo can't reach back into it; `clear` drops it.
  const baselineRef = useRef<HTMLImageElement | null>(null);

  /** Every stroke on the canvas, oldest first — restored work under this session's. */
  const allStrokes = () => [...restoredRef.current, ...strokesRef.current];

  const regionRef = useRef({ width: 0, height: 0 });
  const surfaceRef = useRef({ width: 0, height: 0 });
  // See `resize()` — the backing-store size it last actually applied, so a `ResizeObserver` notification
  // carrying the same size (including one `resize()` itself indirectly caused) is a no-op instead of a fresh
  // repaint.
  const appliedSizeRef = useRef({ realWidth: 0, realHeight: 0 });
  const [regionSize, setRegionSize] = useState({ width: 0, height: 0 });
  const [zoomed, setZoomed] = useState(false);
  // Whether there's a page to show behind the ink and a rectangle of it to zoom into — true in every real
  // usage; kept as a guard only so a caller missing either doesn't get an editor that opens onto nothing.
  const canEdit = Boolean(pageImageUrl && crop);

  // See `printedBackground`. Gated on having been opened at least once, kept true after that: the backdrop
  // exists only inside the zoomed editor, and an exam page can carry a dozen drawing slots — mounting the
  // `<img>` up front would start a dozen page-image downloads for editors most students never open. No blob
  // URL needed here (unlike the earlier SVG version): `pageImageUrl` already points at a plain servable PNG.
  const [backdropWanted, setBackdropWanted] = useState(false);
  useEffect(() => {
    if (zoomed) setBackdropWanted(true);
  }, [zoomed]);

  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');
  // `strokes`/`redo` drive undo and redo and count only THIS session's; `carried` is whether there is also
  // restored work on the canvas, which only Clear and the eraser can reach.
  const [counts, setCounts] = useState({ strokes: 0, redo: 0, carried: false });
  const toolRef = useRef(tool);
  toolRef.current = tool;
  const activeErasesRef = useRef(false);

  // Routed through a ref (plain assignment on every render, not an effect) so the pointer-listener effect
  // below doesn't need `eraseAt` in its dependency array — it chains to the `onChange` prop, whose identity
  // can change on any parent render, which would otherwise tear down and reattach every pointer listener
  // mid-stroke.
  const eraseAtRef = useRef<(points: InkPoint[]) => void>(() => {});
  // A contact's lift can arrive as neither `pointerup` nor a `buttons === 0` move (see `finalizeStroke`).
  // `idleFinalizeTimeoutRef` is a long backstop that finalizes an abandoned stroke so it isn't lost;
  // `lastActivePointRef`/`lastActiveEventAtRef` back the distance+time check in `isContinuation` below that
  // decides whether a delayed sample continues the current stroke or starts a new one.
  const idleFinalizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivePointRef = useRef<InkPoint | null>(null);
  const lastActiveEventAtRef = useRef(0);
  // `getBoundingClientRect()` is a layout-forcing read: if anything on the page has a pending
  // layout-invalidating write at that instant, it triggers a full synchronous reflow. Calling it on every
  // single `pointermove` sample (hundreds per second on a fast stroke) turned into exactly that — measured
  // live as a 525ms handler. Cached across the whole editing session (not reset per stroke — an earlier
  // version did that and paid for it at the worst possible time: a `setCounts` commit from the previous
  // stroke could leave layout dirty, and the very next stroke's `pointerdown` would then force a synchronous
  // reflow right as the pen touched down, corrupting its first samples). Only invalidated by an actual
  // resize; see `resize()` and `activeScale` below.
  const activeScaleRef = useRef<{ rect: DOMRect; scaleX: number; scaleY: number } | null>(null);

  const ratio = () => Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

  const surfaceContext = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: false }) as CanvasRenderingContext2D | null;
    if (ctx) ctx.imageSmoothingEnabled = false;
    return ctx;
  };

  const drawScale = useCallback(
    () => (regionRef.current.width > 0 ? surfaceRef.current.width / regionRef.current.width : 1),
    [],
  );

  const getCommittedContext = useCallback(() => {
    const layer = committedRef.current;
    if (!layer) return null;
    const context = layer.getContext('2d');
    if (!context) return null;
    context.imageSmoothingEnabled = false;
    context.setTransform(1, 0, 0, 1, 0, 0);
    const scale = ratio() * drawScale();
    context.setTransform(scale, 0, 0, scale, 0, 0);
    return context;
  }, []);

  // Both callers' contexts are already transformed so one unit is one region-space unit, and the baseline
  // image covers exactly the region, so it draws at the region's own dimensions in either.
  const paintBaseline = useCallback((context: CanvasRenderingContext2D) => {
    const baseline = baselineRef.current;
    const region = regionRef.current;
    if (!baseline || region.width <= 0 || region.height <= 0) return;
    context.drawImage(baseline, 0, 0, region.width, region.height);
  }, []);

  const repaintCommitted = useCallback(() => {
    const layer = committedRef.current;
    const context = getCommittedContext();
    if (!layer || !context) return;

    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, layer.width, layer.height);
    context.restore();

    paintBaseline(context);
    for (const stroke of allStrokes()) {
      paintStroke(context, stroke);
    }
  }, [getCommittedContext, paintBaseline]);

  // Repaints the whole surface on every sample: committed layer, then the active stroke's current outline.
  // perfect-freehand's outline depends on the entire stroke so far, not just the newest point, so there's no
  // cheaper incremental option — a `drawImage` plus one `getStroke()` over a single stroke's points stays
  // comfortably synchronous.
  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const committed = committedRef.current;
    const context = canvas ? surfaceContext(canvas) : null;
    if (!canvas || !committed || !context) return;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(committed, 0, 0);

    const active = activeRef.current;
    if (active && !activeErasesRef.current && active.points.length > 0) {
      const scale = ratio() * drawScale();
      context.setTransform(scale, 0, 0, scale, 0, 0);
      paintActiveStroke(context, active);
    }
  }, [drawScale]);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const committed = committedRef.current;
    const container = containerRef.current;
    if (!canvas || !committed || !container) return;

    const scale = ratio();
    const width = container.clientWidth;
    const boxHeight = container.clientHeight;
    if (width <= 0 || boxHeight <= 0) return;

    const realWidth = Math.round(width * scale);
    const realHeight = Math.round(boxHeight * scale);

    // `ResizeObserver` fires again on any box change it observes — including one this function itself causes
    // by setting `canvas.width`/`height` (the backing store), which can nudge the browser's layout of the
    // canvas box by a sub-pixel amount before it resettles. Bailing out once the backing store is already at
    // the target size avoids redoing that work needlessly.
    const applied = appliedSizeRef.current;
    if (applied.realWidth === realWidth && applied.realHeight === realHeight) return;
    appliedSizeRef.current = { realWidth, realHeight };

    // The canvas's on-screen rect is about to change — see `activeScaleRef` (a mid-stroke resize is rare,
    // but a stale cached rect would misplace the rest of that stroke).
    activeScaleRef.current = null;

    canvas.width = realWidth;
    canvas.height = realHeight;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${boxHeight}px`;

    committed.width = realWidth;
    committed.height = realHeight;

    surfaceRef.current = { width, height: boxHeight };

    repaintCommitted();
    paint();
  }, [repaintCommitted, paint]);

  const measureRegion = useCallback(() => {
    const el = regionElRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const regionHeight = el.clientHeight;
    if (width <= 0 || regionHeight <= 0) return;

    // Points are stored in region space, so a region that changes size (the window resizing, the exam page
    // reflowing around it) would leave every stroke already drawn sitting at the wrong place and scale.
    const previousRegion = regionRef.current;
    const region = { width, height: regionHeight };
    regionRef.current = region;
    if (previousRegion.width > 0 && previousRegion.height > 0 && (previousRegion.width !== width || previousRegion.height !== regionHeight)) {
      const rescale = (stroke: Stroke) => rescaleStroke(stroke, previousRegion, region);
      strokesRef.current = strokesRef.current.map(rescale);
      restoredRef.current = restoredRef.current.map(rescale);
      redoRef.current = redoRef.current.map(rescale);
    }

    setRegionSize(previous =>
      previous.width === width && previous.height === regionHeight ? previous : region,
    );
  }, []);

  // Toolbar button states (undo/redo enabled/disabled) are local UI, not the parent notification — keep
  // them responsive on every change (stroke, undo, redo, erase), independent of `emit` (which notifies the
  // parent and only ever runs once, when the editor closes).
  const syncCounts = useCallback(() => {
    setCounts({
      strokes: strokesRef.current.length,
      redo: redoRef.current.length,
      carried: restoredRef.current.length > 0 || Boolean(baselineRef.current),
    });
  }, []);

  // Runs once per editing session, when the zoomed editor closes (see the `zoomed` watcher below) — never
  // mid-stroke, so there's no need for the "only newly-added strokes" incremental accumulator an earlier
  // version had here: painting every stroke fresh, once, is cheap enough on its own.
  const emit = useCallback(() => {
    const seq = ++emitSeqRef.current;

    const strokes = allStrokes();
    syncCounts();
    const box = regionRef.current;
    if (box.width <= 0 || box.height <= 0) return;
    // Saved alongside the PNG so the next session gets the strokes back, not a bitmap — see `SavedStroke`.
    // Nothing is saved while a legacy baseline image is in play, though: the export then contains ink that
    // no stroke describes, and saving a partial stroke list would make the NEXT load prefer it and drop
    // everything that was only in the image. The whole drawing stays image-shaped until Clear ends it.
    const saved = baselineRef.current ? [] : strokes.map(stroke => toSavedStroke(stroke, box));
    if (strokes.length === 0 && !baselineRef.current) {
      onChange(null, saved);
      return;
    }
    const scale = Math.min(
      DETAIL,
      Math.sqrt(MAX_EXPORT_PIXELS / (box.width * box.height)),
      MAX_EXPORT_SIDE / box.width,
      MAX_EXPORT_SIDE / box.height,
    );
    const width = Math.round(box.width * scale);
    const height = Math.round(box.height * scale);

    const output = document.createElement('canvas');
    output.width = width;
    output.height = height;
    const context = output.getContext('2d');
    if (!context) return;
    context.setTransform(scale, 0, 0, scale, 0, 0);
    paintBaseline(context);
    for (const stroke of strokes) paintStroke(context, stroke);

    // `toDataURL` encodes the PNG synchronously on the main thread — measured live as the source of
    // recurring long-handler violations. `toBlob` performs the same encode without blocking the caller; the
    // result still reaches `onChange` as a data URL, so nothing downstream needs to change.
    output.toBlob(blob => {
      if (seq !== emitSeqRef.current) return; // superseded by a newer export while this one was encoding
      if (!blob) {
        onChange(null, saved);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (seq !== emitSeqRef.current) return;
        onChange(reader.result as string, saved);
      };
      reader.readAsDataURL(blob);
    }, 'image/png');
  }, [onChange, paintBaseline, syncCounts]);

  const canvasScale = (canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const region = regionRef.current;
    return {
      rect,
      scaleX: rect.width > 0 && region.width > 0 ? region.width / rect.width : 1,
      scaleY: rect.height > 0 && region.height > 0 ? region.height / rect.height : 1,
    };
  };

  // See `activeScaleRef` — computes fresh once per editing session (cache reset only on `resize()`), instead
  // of forcing a layout on every sample.
  const activeScale = (canvas: HTMLCanvasElement) => {
    if (!activeScaleRef.current) activeScaleRef.current = canvasScale(canvas);
    return activeScaleRef.current;
  };

  const pointsFrom = (canvas: HTMLCanvasElement, native: PointerEvent): InkPoint[] => {
    const { rect, scaleX, scaleY } = activeScale(canvas);
    const samples = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const events = samples.length > 0 ? samples : [native];
    return events.map(
      sample =>
        [(sample.clientX - rect.left) * scaleX, (sample.clientY - rect.top) * scaleY, sample.pressure] as InkPoint,
    );
  };

  const eraseAt = useCallback(
    (points: InkPoint[]) => {
      const reach = 8 / drawScale();
      const survives = (stroke: Stroke) =>
        !points.some(([px, py]) =>
          stroke.points.some(([ax, ay], index) => {
            const next = stroke.points[index + 1];
            return next
              ? distanceToSegment(px, py, ax, ay, next[0], next[1]) <= reach + stroke.size / 2
              : Math.hypot(px - ax, py - ay) <= reach + stroke.size / 2;
          }),
        );
      const before = strokesRef.current.length + restoredRef.current.length;
      // Restored strokes erase exactly like this session's — that's the whole reason they're strokes.
      strokesRef.current = strokesRef.current.filter(survives);
      restoredRef.current = restoredRef.current.filter(survives);
      if (strokesRef.current.length + restoredRef.current.length !== before) {
        repaintCommitted();
        paint();
        syncCounts();
      }
    },
    [drawScale, paint, repaintCommitted, syncCounts],
  );
  eraseAtRef.current = eraseAt;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Drawing only ever happens in the zoomed editor — no pointer listeners at all otherwise, only the
    // click-to-zoom handler in the JSX below.
    if (!canvas || !zoomed) return;

    const ctx = surfaceContext(canvas);
    if (ctx) ctx.clearRect(0, 0, 1, 1);

    const now = () =>
      typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();

    const armIdleFinalize = () => {
      if (idleFinalizeTimeoutRef.current) clearTimeout(idleFinalizeTimeoutRef.current);
      idleFinalizeTimeoutRef.current = setTimeout(() => {
        if (activeRef.current) finalizeStroke();
      }, STROKE_IDLE_MS);
    };

    const trackActivePoint = (point: InkPoint) => {
      lastActivePointRef.current = point;
      lastActiveEventAtRef.current = now();
      armIdleFinalize();
    };

    /**
     * Whether a newly-arrived point should be treated as continuing the current active stroke, or as the
     * start of a separate one — see the doc comment on `lastActivePointRef`/`idleFinalizeTimeoutRef` above
     * for why this needs both a time gap and a distance, not either alone.
     */
    const isContinuation = (point: InkPoint) => {
      const last = lastActivePointRef.current;
      if (!last) return true;
      if (now() - lastActiveEventAtRef.current <= CONTINUATION_GRACE_MS) return true;
      return Math.hypot(point[0] - last[0], point[1] - last[1]) <= CONTINUATION_MAX_JUMP;
    };

    /**
     * Starts a stroke from any event that represents real contact (down, or a move carrying contact that
     * never got a proper `pointerdown` — see `onPointerMove`). Pulled out of `onPointerDown` so both paths
     * stay identical.
     */
    const beginStroke = (e: PointerEvent) => {
      // CAPTURE POINTER: CRITICAL to stop missing points in fast curves
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {}

      const points = pointsFrom(canvas, e);
      const isEraser = toolRef.current === 'eraser' || isEraserButton(e);
      activeErasesRef.current = isEraser;
      trackActivePoint(points.at(-1) ?? points[0]!);

      if (isEraser) {
        activeRef.current = { points, size: 0 };
        eraseAtRef.current(points);
        return;
      }

      redoRef.current = [];
      activeRef.current = { points, size: NIB_SIZE };

      paint();
    };

    // Commits whatever is in `activeRef` and clears it. Used by a normal `pointerup`, by the implicit-lift
    // detection in `onPointerMove` (`e.buttons === 0`), and wherever a stroke has to end before a new one
    // starts — some platforms never dispatch `pointerup` and instead report the next touch as a `pointermove`
    // continuation of the same pointerId.
    const finalizeStroke = () => {
      if (idleFinalizeTimeoutRef.current) {
        clearTimeout(idleFinalizeTimeoutRef.current);
        idleFinalizeTimeoutRef.current = null;
      }
      const active = activeRef.current;
      activeRef.current = null;
      if (!active) return;

      if (!activeErasesRef.current && active.points.length > 0) {
        // Cache the final outline (proper tapered end cap, `last: true`) once, here, rather than
        // recomputing it on every future repaint/undo/redo.
        active.outline = getStroke(active.points, freehandOptions(active.size, true));
        strokesRef.current = [...strokesRef.current, active];
        const commitCtx = getCommittedContext();
        if (commitCtx) paintStroke(commitCtx, active);
      }

      activeErasesRef.current = false;
      paint();
      syncCounts();
      // The parent is notified once, when editing closes (see the `zoomed` watcher below) — never per stroke.
    };

    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // A still-active stroke here means the previous contact's lift was never reported (see
      // `finalizeStroke`'s doc comment) — commit it rather than silently discard it before starting fresh.
      if (activeRef.current) finalizeStroke();

      beginStroke(e);
    };

    const onPointerMove = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Trust `buttons`, not the event type, for contact state: some platforms report a lift as a
      // `pointermove` with `buttons === 0` rather than a `pointerup`. Finalize here instead of drawing a
      // hovering pen's position as ink.
      if (e.buttons === 0) {
        if (activeRef.current) finalizeStroke();
        // Opportunistically warm `activeScaleRef` here too — a pen typically hovers before making contact,
        // so this pays for `getBoundingClientRect()` (see `activeScale`) during a moment that isn't
        // latency-critical, rather than leaving the very first sample of the next stroke to pay for it.
        if (!activeScaleRef.current) activeScaleRef.current = canvasScale(canvas);
        return;
      }

      const points = pointsFrom(canvas, e);

      // No stroke active, or the active one has gone quiet for a while and this sample lands somewhere
      // else entirely — either way, this is the start of a separate stroke, not a continuation. See
      // `isContinuation`'s doc comment: distance is what disambiguates "same slow stroke, delayed sample"
      // from "an unrelated new touch reported as a move because the platform never sent pointerdown/up".
      if (!activeRef.current || !isContinuation(points[0]!)) {
        if (activeRef.current) finalizeStroke();
        beginStroke(e);
        return;
      }

      const active = activeRef.current;
      if (activeErasesRef.current) {
        eraseAtRef.current(points);
        return;
      }

      active.points.push(...points);
      trackActivePoint(points.at(-1) ?? points[0]!);
      // Synchronous drawing: ink flows out of the pen instantly, no rAF batching.
      paint();
    };

    const onPointerUp = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {}

      const active = activeRef.current;
      if (!active) return;

      const points = pointsFrom(canvas, e);
      if (activeErasesRef.current) {
        eraseAtRef.current(points);
      } else {
        active.points.push(...points);
        // No `paint()` here: it would recompute the full outline (perfect-freehand's most expensive step,
        // costed by total point count — largest it'll ever be for this stroke) for an in-progress frame
        // that `finalizeStroke` immediately supersedes with the real, tapered-end-cap outline. Measured live
        // as a CPU spike landing exactly on release — this call was pure wasted work.
      }

      finalizeStroke();
    };

    canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
    canvas.addEventListener('pointermove', onPointerMove, { passive: false });
    canvas.addEventListener('pointerup', onPointerUp, { passive: false });
    canvas.addEventListener('pointercancel', onPointerUp, { passive: false });

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      if (idleFinalizeTimeoutRef.current) clearTimeout(idleFinalizeTimeoutRef.current);
    };
    // `zoomed` toggles both which <canvas> is mounted (see `zoomOverlay`'s portal) and whether listeners
    // attach at all. `eraseAt` is deliberately excluded — see the ref declared above; `syncCounts` has no
    // unstable dependencies so its identity never changes, safe to reference without listing it.
  }, [getCommittedContext, paint, zoomed, syncCounts]);

  useEffect(() => {
    committedRef.current = document.createElement('canvas');
    // A fresh, empty committed layer needs its own fresh `resize()` pass no matter what the target size
    // computes to this time — see the size guard in `resize()`, which would otherwise wrongly skip
    // repainting existing strokes onto this brand-new canvas if the size happens to match the last one.
    appliedSizeRef.current = { realWidth: -1, realHeight: -1 };

    // `measureRegion`/`resize` read `clientWidth`/`clientHeight` — reading a layout-dependent property right
    // after this same effect's own DOM write (mounting/unmounting the zoomed overlay's whole portal subtree)
    // forces the browser to synchronously compute layout for the entire page right then, instead of letting
    // it happen on the next natural layout pass. Measured live as a 418ms "Forced reflow" violation landing
    // exactly on zoom-open. Deferring one frame lets that layout happen normally, off the critical path.
    const raf = requestAnimationFrame(() => {
      measureRegion();
      resize();
    });

    if (typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const observer = new ResizeObserver(() => {
      measureRegion();
      resize();
    });
    if (regionElRef.current) observer.observe(regionElRef.current);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [resize, measureRegion, zoomed]);

  // Restores saved work, ONCE. Everything offered afterwards is ignored: what comes back through these props
  // after the first save is this component's own export, and taking it again would either duplicate the
  // strokes or bake the image onto itself. `regionSize` is in the dependencies because the points are stored
  // as fractions of the region (see `SavedStroke`) and there is nothing to scale them by until it's measured.
  const restoreDoneRef = useRef(false);
  useEffect(() => {
    if (restoreDoneRef.current) return;
    const region = regionRef.current;
    if (region.width <= 0 || region.height <= 0) return;

    if (initialStrokes && initialStrokes.length > 0) {
      restoreDoneRef.current = true;
      restoredRef.current = initialStrokes.map(saved => fromSavedStroke(saved, region));
      repaintCommitted();
      paint();
      syncCounts();
      return;
    }

    // See `initialDrawing` — a draft saved before strokes were, restorable only as a flat image.
    if (!initialDrawing) return;
    restoreDoneRef.current = true;
    const image = new Image();
    image.onload = () => {
      baselineRef.current = image;
      repaintCommitted();
      paint();
      syncCounts(); // restored ink counts for `clear`'s enabled state, even with no strokes drawn yet
    };
    image.src = initialDrawing;
  }, [initialStrokes, initialDrawing, regionSize, repaintCommitted, paint, syncCounts]);

  // This is the ONLY place the parent gets notified — once, when an editing session closes — instead of on
  // a debounce racing active drawing. `wasZoomedRef` distinguishes "just closed" from "mounted already
  // closed" (e.g. a fresh slot with no drawing yet — nothing to emit).
  const wasZoomedRef = useRef(zoomed);
  useEffect(() => {
    if (wasZoomedRef.current && !zoomed) emit();
    wasZoomedRef.current = zoomed;
  }, [zoomed, emit]);

  const undo = () => {
    const last = strokesRef.current.at(-1);
    if (!last) return;
    strokesRef.current = strokesRef.current.slice(0, -1);
    redoRef.current = [...redoRef.current, last];
    repaintCommitted();
    paint();
    syncCounts();
  };

  const redo = () => {
    const last = redoRef.current.at(-1);
    if (!last) return;
    redoRef.current = redoRef.current.slice(0, -1);
    strokesRef.current = [...strokesRef.current, last];
    // Unlike undo, redo only ever appends a stroke that's already fully painted before (it was on the
    // canvas until the matching undo) — no need for `repaintCommitted`'s full clear-and-redraw-everything.
    const commitCtx = getCommittedContext();
    if (commitCtx) paintStroke(commitCtx, last);
    paint();
    syncCounts();
  };

  const clear = () => {
    strokesRef.current = [];
    restoredRef.current = [];
    redoRef.current = [];
    activeRef.current = null;
    // Clear means clear: restored ink from an earlier session goes too, otherwise the button leaves work
    // behind that no other control can remove (see `baselineRef` — undo/erase only reach this session's).
    baselineRef.current = null;
    repaintCommitted();
    paint();
    syncCounts();
  };

  const toolButton = (
    active: boolean,
    onClick: () => void,
    label: string,
    children: React.ReactNode,
    disabled = false,
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:opacity-40 ${
        active
          ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-slate-700 dark:text-brand-300'
          : 'border-transparent text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
      }`}
    >
      {children}
    </button>
  );

  const toolbar = (
    <>
      {toolButton(tool === 'pen', () => setTool('pen'), t('StudentExam.canvas.pen'), <Pen size={13} />)}
      {toolButton(tool === 'eraser', () => setTool('eraser'), t('StudentExam.canvas.eraser'), <Eraser size={13} />)}
      <span className="mx-0.5 h-4 w-px bg-slate-200 dark:bg-slate-600" />
      {toolButton(false, undo, t('StudentExam.canvas.undo'), <Undo2 size={13} />, counts.strokes === 0)}
      {toolButton(false, redo, t('StudentExam.canvas.redo'), <Redo2 size={13} />, counts.redo === 0)}
      {toolButton(
        false,
        clear,
        t('StudentExam.canvas.clear'),
        <Trash2 size={13} />,
        counts.strokes === 0 && !counts.carried,
      )}
      <span className="mx-0.5 h-4 w-px bg-slate-200 dark:bg-slate-600" />
      {toolButton(
        zoomed,
        () => setZoomed(!zoomed),
        t(zoomed ? 'StudentExam.canvas.zoomClose' : 'StudentExam.canvas.zoom'),
        zoomed ? <ZoomOut size={13} /> : <ZoomIn size={13} />,
      )}
    </>
  );

  const canvas = (
    <canvas
      ref={canvasRef}
      onContextMenu={event => event.preventDefault()}
      className="block outline-none"
      style={{
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        willChange: 'transform',
        contain: 'strict',
        // The inline preview isn't drawable — a plain pointer cursor signals "click to open the editor"
        // instead of implying you can draw right here.
        cursor: !zoomed ? 'pointer' : tool === 'eraser' ? ERASER_CURSOR : PEN_CURSOR,
      }}
    />
  );

  // A PNG (`pageImageUrl`), cropped and blown up with plain CSS percentages — see this component's own
  // `pageImageUrl` doc comment for why that's cheap for a raster where it wasn't for the live exam SVG an
  // earlier version of this used instead (that one needed a narrowed `viewBox`, a stripped text-selection
  // layer, and a blob URL per slot just to avoid re-rasterizing vector paths on every stroke; none of that
  // machinery exists here, because scaling and cropping an already-rasterized bitmap is a compositor-cheap
  // texture sample no matter how far it's stretched). `max-w-none` defeats the preflight `img { max-width:
  // 100% }`, which would otherwise clamp the deliberately oversized crop framing below. Gated on
  // `backdropWanted` — see that flag's own comment — so a slot never opened never even requests its page
  // image.
  const printedBackground = backdropWanted && pageImageUrl && crop && (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-white [transform:translateZ(0)]">
      <img
        src={pageImageUrl}
        alt=""
        draggable={false}
        className="absolute block max-w-none"
        style={{
          left: `${(-crop.left / crop.width) * 100}%`,
          top: `${(-crop.top / crop.height) * 100}%`,
          width: `${100 / crop.width}%`,
          height: `${100 / crop.height}%`,
        }}
      />
    </div>
  );

  const surface = (
    <div ref={containerRef} className="absolute inset-0 touch-none select-none">
      {canvas}
    </div>
  );

  const aspect = regionSize.height > 0 ? regionSize.width / regionSize.height : 1;
  // The height budget (78vh normally) shrinks when a prompt is shown, leaving the prompt picture(s) real
  // room above the drawing box instead of letting everything together overflow the viewport.
  const hasPrompt = Boolean(backdropWanted && pageImageUrl && promptCrops && promptCrops.length > 0);
  const zoomBoxHeightVh = hasPrompt ? 66 : 78;
  const zoomBoxWidth = `min(94vw, calc(${zoomBoxHeightVh}vh * ${aspect}), ${regionSize.width * MAX_ZOOM}px)`;
  // Cropped the SAME way `printedBackground` crops the drawing surface itself, off the same page image —
  // see `DrawingCanvasProps.promptCrops`'s own doc comment for why these are pictures, not re-typed text.
  // A small GAP between each ancestor level (`gap-1`, distinct pictures for distinct printed blocks, e.g.
  // task "5"'s own intro vs. "5.a"'s own prompt), but NONE between the LAST one and the drawing box below —
  // that seam is still meant to read as one continuous crop (see `zoomOverlay`), where consecutive ancestor
  // levels are closer to two separate quotes than one continuous run of text.
  //
  // PADDED ON THE SIDES, unlike the drawing box below — a small inset margin around the WHOLE stack (not
  // per picture, which already gets its own vertical breathing room from `gap-1`), so the text doesn't run
  // flush to the frame's edge the way live ink flush against the printed answer box is supposed to. The
  // OUTER frame this sits in (see `zoomOverlay`) stays the same width as the drawing box either way — only
  // the pictures inside are inset, not the frame.
  const taskPrompt = hasPrompt && (
    <div className="flex w-full flex-col gap-4 bg-white px-3 pt-2 dark:bg-slate-800">
      {promptCrops!.map((entry, index) => (
        <div key={index} className="relative w-full overflow-hidden" style={{ aspectRatio: `${entry.aspect}` }}>
          <img
            src={entry.pageImageUrl}
            alt=""
            draggable={false}
            className="absolute block max-w-none"
            style={{
              left: `${(-entry.crop.left / entry.crop.width) * 100}%`,
              top: `${(-entry.crop.top / entry.crop.height) * 100}%`,
              width: `${100 / entry.crop.width}%`,
              height: `${100 / entry.crop.height}%`,
            }}
          />
        </div>
      ))}
    </div>
  );
  const zoomOverlay = zoomed && canEdit && typeof document !== 'undefined' && (
    <>
      {createPortal(
        <div
          // `backdrop-blur` here would sit over the entire viewport, including the exam page's giant SVG
          // behind it (tens of thousands of DOM nodes) — one of the most compositor-expensive CSS effects
          // there is, over one of the largest possible backdrops. Unlike a layout cost, this doesn't show up
          // as a "Forced reflow" violation (a different phase of the rendering pipeline), which is exactly
          // why every stroke-boundary lag investigation this session found nothing there: this cost is paint
          // /composite, not script or layout. It also only exists because of the zoomed-editor model (the old
          // inline-drawable canvas never had a full-viewport backdrop at all) — matching the severe
          // regression showing up specifically after that change. The plain dark scrim alone still
          // sufficiently distinguishes the editor from the page behind it.
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
          onPointerDown={event => {
            if (event.target === event.currentTarget) setZoomed(false);
          }}
        >
          <div className="flex max-h-full flex-col items-center gap-2">
            {/* One shared frame around BOTH pictures, not one each — see `taskPrompt`'s own comment on why
             * they're meant to read as a single continuous crop. Rounding/shadow/ring live here, on the
             * frame, so the seam between the two internal boxes is invisible rather than looking like two
             * cards stacked with matching corners. */}
            <div
              className="flex w-full flex-col overflow-hidden rounded-md shadow-2xl ring-1 ring-slate-900/20"
              style={{ width: zoomBoxWidth }}
            >
              {taskPrompt}
              <div className="relative w-full" style={{ aspectRatio: `${aspect}` }}>
                {printedBackground}
                {surface}
              </div>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 shadow-lg dark:border-slate-600 dark:bg-slate-800">
              {toolbar}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );

  return (
    <div ref={regionElRef} className="group relative h-full w-full touch-none select-none">
      {!zoomed && (
        <div
          className="absolute inset-0"
          onClick={canEdit ? () => setZoomed(true) : undefined}
          role={canEdit ? 'button' : undefined}
          aria-label={canEdit ? t('StudentExam.canvas.zoom') : undefined}
        >
          {surface}
        </div>
      )}
      {zoomOverlay}
    </div>
  );
});
